import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "text-center fs-5 fw-semibold mb-5" }
const _hoisted_2 = {
  key: 0,
  class: "form-text text-muted mb-0 mt-2"
}

import { ConfirmationModal } from "@/components/shared/modal";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import { swalErrNotification } from "@/core/helpers/utils";
import {
  useDateTime,
  getGlobalDefaultDate,
} from "@/core/hooks/use-date-time-hook";
import { useEnv } from "@/core/hooks/useEnv";
import {
  IBienBanGiaiTheResponseBase,
  IPhieuNhapKhoResponseBase,
  IPhieuXuatKhoResponseBase,
} from "@/core/interfaces/ApiResponses";
import { PhieuNhapKhoService } from "@/core/services/PhieuNhapKho.service";
import PhieuXuatKhoService from "@/core/services/Receipt.service";
import DivCol from "@/layout/_shared/DivCol.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import { computed, PropType, ref, watch } from "vue";
import { useStore } from "vuex";


export default /*#__PURE__*/_defineComponent({
  __name: 'ConfirmHoanThanh',
  props: {
  bienBanData: {
    type: Object as PropType<IBienBanGiaiTheResponseBase | null>,
    default: null,
  },
  for: {
    type: String as PropType<"phieu_nhap" | "phieu_xuat" | "bien_ban_giai_the">,
    default: "bien_ban_giai_the",
  },
  phieuNhapData: {
    type: Object as PropType<IPhieuNhapKhoResponseBase | null>,
    default: null,
  },
  phieuXuatData: {
    type: Object as PropType<IPhieuXuatKhoResponseBase | null>,
    default: null,
  },
  show: {
    type: Boolean as PropType<boolean>,
    required: true,
  },
  isApprovalFlow: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
},
  emits: ["close", "confirm"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const completeDate = ref(getGlobalDefaultDate());

const store = useStore();

const currentYear = computed(() => store.getters.getSelectedYear);

watch(
  () => props.show,
  () => {
    if (props.show) {
      const effectiveDate = getGlobalDefaultDate();
      const selectedYear = currentYear.value
        ? parseInt(currentYear.value)
        : effectiveDate.getFullYear();
      completeDate.value = new Date(
        selectedYear,
        effectiveDate.getMonth(),
        effectiveDate.getDate()
      );
    }
  }
);

const sendRequest = async () => {
  if (props.for === "phieu_nhap") {
    if (props.isApprovalFlow) {
      // For approval flow, call duyetPhieuNhapKho with ngay_hoan_thanh
      await PhieuNhapKhoService.duyetPhieuNhapKho({
        id: props.phieuNhapData?.id ?? 0,
        ngay_hoan_thanh: completeDate.value,
      });
    } else {
      // For completion flow, call completePhieuNhapKho
      await PhieuNhapKhoService.completePhieuNhapKho({
        id: props.phieuNhapData?.id ?? 0,
        ngay_hoan_thanh: completeDate.value,
      });
    }

    return;
  }

  if (props.for === "phieu_xuat") {
    await PhieuXuatKhoService.duyetPhieuXuat({
      id: props.phieuXuatData?.id ?? 0,
      ngay_hoan_thanh: completeDate.value,
    });

    return;
  }
};

const confirmHoanThanhBienBanHandler = async () => {
  // For bien_ban_giai_the, just emit - no API call (handled by hooks)
  if (props.for === "bien_ban_giai_the") {
    emit("confirm", {
      date: completeDate.value,
    });
    return;
  }

  // Keep existing logic for other cases
  try {
    await sendRequest();
    emit("confirm", {
      date: completeDate.value,
    });
  } catch (err) {
    emit("close");

    await swalErrNotification(err, "Có lỗi xảy ra");
  }
};

const header = computed(() => {
  if (props.for === "bien_ban_giai_the")
    return `Hoàn thành biên bản số ${props.bienBanData?.so_bien_ban}?`;

  if (props.for === "phieu_nhap") {
    if (props.isApprovalFlow) {
      return `Nhập kho phiếu số ${props.phieuNhapData?.so_phieu}?`;
    }
    return `Xác nhận cập nhật ngày hoàn thành phiếu nhập số ${props.phieuNhapData?.so_phieu}?`;
  }

  if (props.for === "phieu_xuat") {
    return `Xuất kho phiếu số ${props.phieuXuatData?.so_phieu}?`;
  }

  return "Xác nhận";
});

const { disableDate } = useDateTime();
const { enableInputNgayRaXuong } = useEnv();

const isDatePickerReadonly = computed(() => {
  return props.for === "phieu_nhap" || props.for === "phieu_xuat";
});

const isDatePickerDisabled = computed(() => {
  if (props.for === "bien_ban_giai_the") {
    return !enableInputNgayRaXuong.value;
  }

  return false;
});

const datePickerLabel = computed(() => {
  if (props.for === "phieu_nhap") {
    return "Ngày nhập kho";
  }

  if (props.for === "phieu_xuat") {
    return "Ngày xuất kho";
  }

  return "Ngày ra xưởng";
});

const datePickerHelpText = computed(() => {
  if (props.for === "phieu_nhap" || props.for === "phieu_xuat") {
    return `${datePickerLabel.value} được lấy theo ngày hiện tại và không thể chỉnh sửa.`;
  }

  if (props.for === "bien_ban_giai_the" && isDatePickerDisabled.value) {
    return "Ngày ra xưởng không thể chỉnh sửa.";
  }

  return "";
});

const disableDateValue = (time: Date) => {
  if (["phieu_nhap", "phieu_xuat"].includes(props.for)) {
    return disableDate(time, currentYear.value);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_el_date_picker = _resolveComponent("el-date-picker")!

  return (_openBlock(), _createBlock(_unref(ConfirmationModal), {
    title: "Xác nhận",
    "is-display": __props.show,
    onConfirm: confirmHoanThanhBienBanHandler,
    onClose: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
  }, {
    default: _withCtx(() => [
      _createElementVNode("p", _hoisted_1, _toDisplayString(header.value), 1),
      _createVNode(DivRow, null, {
        default: _withCtx(() => [
          _createVNode(DivCol, {
            cols: 4,
            "additional-class": "d-flex align-items-start"
          }, {
            default: _withCtx(() => [
              _createVNode(LabelCustom, {
                "is-text-muted": false,
                "is-required": true
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(datePickerLabel.value), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(DivCol, { cols: 8 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_date_picker, {
                modelValue: completeDate.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((completeDate).value = $event)),
                class: "w-100",
                readonly: isDatePickerReadonly.value,
                disabled: isDatePickerDisabled.value,
                "disabled-date": disableDateValue,
                format: "DD/MM/YYYY",
                type: "date"
              }, null, 8, ["modelValue", "readonly", "disabled"]),
              (datePickerHelpText.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(datePickerHelpText.value), 1))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["is-display"]))
}
}

})